import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

interface RecognizeOrdersCardProps {
  teamId: string;
  enabled: boolean;
}

export const RecognizeOrdersCard = memo(function RecognizeOrdersCard({
  teamId,
  enabled,
}: RecognizeOrdersCardProps) {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const [recentCount, setRecentCount] = useState("2000");
  const status = useRpcQuery(RedoAdminRpcClientContext, {
    method: "getRecognizeOrdersStatus",
    args: { teamId },
    options: {
      enabled,
      refetchInterval: (query) => {
        if (query.state.data?.status === "RUNNING") {
          return 5000;
        }
        return false;
      },
    },
  });
  const [run, start] = useTriggerLoadWithInput(
    async (count: number | undefined) => {
      const result = await client.runRecognizeOrders({
        teamId,
        recentCount: count,
      });
      await status.refetch();
      if (!result.started) {
        toast(result.message, { variant: "error" });
        return;
      }
      toast("Recognize started in the background.", { variant: "success" });
    },
  );
  const count = Number(recentCount);
  const invalidCount =
    !Number.isSafeInteger(count) || count < 1 || count > 10_000;
  const isRunning = status.data?.status === "RUNNING";
  const isRunningAll = isRunning && status.data?.progress.selected === null;
  const isRunningRecent = isRunning && !isRunningAll;
  const disabled =
    !enabled || status.isPending || status.isError || run.pending || isRunning;
  let runningLabel = "Running…";
  if (isRunning && status.data) {
    const { completed, failed, skipped, selected } = status.data.progress;
    const processed = (completed + failed + skipped).toLocaleString();
    runningLabel = `Running · ${processed} processed`;
    if (selected !== null) {
      runningLabel = `Running · ${processed} of ${selected.toLocaleString()} processed`;
    }
  }

  return (
    <Card title="Recognize existing orders">
      <Flex dir="column" gap="lg" p="lg">
        <Text fontSize="sm" textColor="secondary">
          Research customers from existing outbound orders using the store’s
          Recognize source filters. Eligible research is reused. This does not
          run Recognize Actions or OMS automations.
        </Text>
        {!enabled && (
          <Text fontSize="sm" textColor="secondary">
            Enable Recognize for this store before running.
          </Text>
        )}
        <Flex align="flex-start" dir="column" gap="md">
          <RedoTextInput
            inputTypeHint="number"
            label="Most recent orders"
            max={10_000}
            min={1}
            setValue={setRecentCount}
            step={1}
            value={recentCount}
          />
          <Flex align="center" gap="md" wrap="wrap">
            <RedoButton
              disabled={disabled || invalidCount}
              hierarchy="secondary"
              onClick={() => void start(count)}
              size="sm"
              text="Run most recent"
            />
            {isRunningRecent && (
              <Text fontSize="sm" textColor="secondary">
                {runningLabel}
              </Text>
            )}
          </Flex>
        </Flex>
        <Divider />
        <Flex align="flex-start" dir="column" gap="md">
          <Text fontSize="sm" fontWeight="medium">
            All existing orders
          </Text>
          <Flex align="center" gap="md" wrap="wrap">
            <RedoButton
              disabled={disabled}
              hierarchy="secondary"
              onClick={() => void start(undefined)}
              size="sm"
              text="Run all"
            />
            {isRunningAll && (
              <Text fontSize="sm" textColor="secondary">
                {runningLabel}
              </Text>
            )}
          </Flex>
        </Flex>
        <Divider />
        <Text fontSize="sm" textColor="secondary">
          Up to 10 research runs at once. Source and country filters apply.
        </Text>
        {status.error && (
          <Text fontSize="sm" textColor="error">
            Unable to load run status: {status.error.message}
          </Text>
        )}
        {run.error && (
          <Text fontSize="sm" textColor="error">
            Unable to start research: {run.error.message}
          </Text>
        )}
      </Flex>
    </Card>
  );
});
